import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import moment from 'moment';
import { makeStyles } from '@mui/styles';
import CommentSection from './comments';
import Context from '../context/Context';

import tabStyles from "../styles/TableComponent.module.scss";
import '../styles/table.css';

const _ = require('lodash');
const useStyles = makeStyles({
  table: {
    minWidth: 650,
    "& .MuiTableCell-root": {
      borderBottom: "0px solid rgba(224, 224, 224, 1)",
      boxShadow: "inset -1px 0px 0px #CAE0F9, inset 0px -1px 0px #CAE0F9",
      fontFamily: ["canada-type-gibson", "Roboto", "sans-serif"]
    },
    "& .MuiTableCell-head": {
      backgroundColor: "#F1F5FB",
      color: "#00172F",
      fontStyle: "normal",
      fontWeight: "500",
      lineHeight: "18px",
      fontSize: "14px"
    }
  },
  tableDark: {
    backgroundColor: "#0a0a0a !important",
    "& .MuiTableCell-root": {
      borderBottom: "0px solid rgba(42, 42, 42, 1)",
      boxShadow: "inset -1px 0px 0px #2A2A2A, inset 0px -1px 0px #2A2A2A",
      color: "#E6E6E6 !important",
      fontFamily: ["canada-type-gibson", "Roboto", "sans-serif"]
    },
    "& .MuiTableCell-root[class*='dataColumnBlue']": {
      color: "#000000 !important"
    },
    "& .MuiTableCell-root[class*='dataColumnRed']": {
      color: "#000000 !important"
    },
    "& .MuiTableCell-head": {
      backgroundColor: "#1A1A1A !important",
      color: "#E6E6E6 !important",
      fontStyle: "normal",
      fontWeight: "500",
      lineHeight: "18px",
      fontSize: "14px"
    }
  }
});

const DataTableComponent = props => {
  const classes = useStyles();
  const context = useContext(Context);
  const isDark = context.colorTheme === 'dark';
  // Will remove after cheking the Compare weeks' UX
  const formatDateHeader = dateStr => {
    const parsedDate = moment(dateStr);
    if (parsedDate.isValid()) {
      /* istanbul ignore next */
      if (props.origin === "weeks") {
        return parsedDate.format('ddd');
      } else {
        return parsedDate.format('MM/DD');
      }
    }
    return dateStr;
  };

  const formatWeekDays = dates => {
    const initDate = moment(dates[0]).format('MM/DD');
    const lastDate = moment(dates[dates.length - 1]).format('MM/DD');
    return `${initDate} - ${lastDate}`;
  };

  const formatPeriod = dates => {
    const initDate = moment(dates[0]).format('MMMM DD');
    const lastDate = moment(dates[dates.length - 1]).format('MMMM DD');
    return `${initDate} - ${lastDate}`;
  };

  const table = props.table;

  const getCssClass = (cell, cellVal, row, idx) => {
    let cellCssClass = tabStyles.dataColumn;
    if (props.origin === "weeks") {
      const isRed =
        cell === "demand" &&
        cellVal !== null &&
        !!table[row].data["sales"] &&
        Math.abs(cellVal) > table[row].data["sales"][idx];
      const isBlue =
        cell === "received" &&
        cellVal != null && cellVal !== '' &&
        table[row].manualAdjqty !== undefined &&
        table[row].manualAdjqty[idx] != null && table[row].manualAdjqty[idx] !== 0;
      if (isRed) {
        cellCssClass = tabStyles.dataColumnRed;
      } else if (isBlue) {
        cellCssClass = tabStyles.dataColumnBlue;
      }
      if (cellVal === null) {
        cellCssClass = cellCssClass + ' ' + tabStyles.textDim;
      }
      if (moment(table[row]?.date?.[idx]).isSame(new Date(), 'day')) {
        cellCssClass = cellCssClass + ' ' + tabStyles.borderLeftRight;
        if (cell === 'date')
          cellCssClass = cellCssClass + ' ' + tabStyles.borderTop;
        else if (cell === 'sales')
          cellCssClass = cellCssClass + ' ' + tabStyles.borderBottom;
      }
    }
    return cellCssClass;
  };

  const dayNames = ["Sat", "Sun", "Mon", "Tue", "Wed", "Thu", "Fri"];

  const getSumCellValue = (cell, currentRow) => {
    if (props.itemNum === 0) return null;
    if (cell === 'date') return '';

    /* istanbul ignore next */
    if (!currentRow?.data) return '';
    const series = currentRow.data[cell];
    /* istanbul ignore next */
    if (!Array.isArray(series)) return '';

    if (cell === 'inventory' && props.origin === "weeks") {
      let nonNullInv = _.without(currentRow.data[cell], null);
      if (nonNullInv.length === 0)
        return '';

      return Math.round(_.mean(nonNullInv));
    }
    const nonNullValues = _.without(series, null, undefined);
    if (nonNullValues.length === 0) return '';
    return _.sum(currentRow.data[cell]);
  };

  return (
    <TableContainer component={Paper} style={isDark ? { backgroundColor: '#0a0a0a' } : {}}>
        <Table
          sx={{ minWidth: 650 }}
          size="small"
          className={isDark ? classes.tableDark : classes.table}
          aria-label="simple table"
          style={isDark ? { backgroundColor: '#0a0a0a' } : {}}
        >
          <TableHead style={isDark ? { backgroundColor: '#1A1A1A' } : {}}>
            {Object.keys(table).map((row, val) => (
              val === 0 &&
              <TableRow
                key={row}
                className={tabStyles.tabHeader}
                style={isDark ? { backgroundColor: '#1A1A1A' } : {}}
              >
                {props.origin === "weeks" && (table[row].week ?
                  (
                    <TableCell
                      className={tabStyles.moveLeft}
                      style={isDark ? { backgroundColor: '#1A1A1A', color: '#E6E6E6' } : {}}
                    >
                      Week / {table[row].year}
                    </TableCell>
                  )
                  : (
                    <TableCell
                      className={tabStyles.moveLeft}
                      style={isDark ? { backgroundColor: '#1A1A1A', color: '#E6E6E6' } : {}}
                    >
                      Week{table[row].year}
                    </TableCell>
                  )
                )}
                {props.origin !== "weeks" && (
                  <TableCell
                    className={tabStyles.moveLeft}
                    style={isDark ? { backgroundColor: '#1A1A1A', color: '#E6E6E6' } : {}}
                  >
                    Period
                  </TableCell>
                )}
                <TableCell
                  style={isDark ? { backgroundColor: '#1A1A1A', color: '#E6E6E6' } : {}}
                >
                  Area
                </TableCell>
                {props.origin === "weeks" && dayNames.map((cell, idx) => (
                  <TableCell
                    key={cell}
                    align="right"
                    style={isDark ? { backgroundColor: '#1A1A1A', color: '#E6E6E6' } : {}}
                  >
                    {dayNames[idx]}
                  </TableCell>
                ))}
                {props.origin !== "weeks" && table[row].date.map(date => (
                  <TableCell
                    key={date}
                    align="right"
                    style={isDark ? { backgroundColor: '#1A1A1A', color: '#E6E6E6' } : {}}
                  >
                    {formatDateHeader(date)}
                  </TableCell>
                ))}
                <TableCell
                  align="right"
                  style={isDark ? { backgroundColor: '#1A1A1A', color: '#E6E6E6' } : {}}
                >
                  Total
                </TableCell>
              </TableRow>))
            }
          </TableHead>
          <TableBody style={isDark ? { backgroundColor: '#0a0a0a' } : {}}>
            {Object.keys(table).map(row => (
              Object.keys(table[row].data).map((cell, value) => (
                <TableRow
                  key={cell}
                  className={
                    value === Object.keys(table[row].data).length - 1
                      ? tabStyles.rowData
                      : ''
                  }
                  style={isDark ? { backgroundColor: '#0a0a0a' } : {}}
                >
                  {value === 0 &&
                    <TableCell
                      rowSpan={Object.keys(table[row].data).length}
                      className={tabStyles.tabFirstColumn}
                    >
                      {table[row].week &&
                        <>
                          <div>
                            <span
                              className={tabStyles.calendarWeek}
                            >
                              {props.origin === 'weeks'
                                ? `Week ${table[row].week}`
                                : formatPeriod(table[row].date)
                              }
                            </span>
                            {props.origin === 'weeks' && (
                              <span
                                className={tabStyles.calendarWeekDay}
                              >
                                {formatWeekDays(table[row].date)}
                              </span>
                            )}
                          </div>
                          {props.origin === "weeks" &&
                            <CommentSection commentObj={table[row].commentObj} isDark={isDark} />
                          }
                        </>
                      }
                    </TableCell>}
                  <TableCell
                    key={`cell-label-${cell}`}
                    className={tabStyles.dataColumn}
                    data-row={cell}
                    style={isDark ? { backgroundColor: '#0a0a0a' } : {}}
                  >
                    {cell}
                  </TableCell>
                  {table[row].data[cell].map((cellVal, idx) => {
                    const cellClass = getCssClass(cell, cellVal, row, idx);
                    const hasCustomBg = cellClass.includes(tabStyles.dataColumnBlue) || cellClass.includes(tabStyles.dataColumnRed);
                    const displayValue = cellVal === null || cellVal === undefined ? '' : cellVal;

                    return (
                      <TableCell
                        key={`${cell}-${idx}`}
                        align="right"
                        className={cellClass + ' ' + tabStyles.dataCellTypography}
                        data-row={cell}
                        style={{
                          ...(isDark && hasCustomBg ? { color: '#000000' } : {}),
                          ...(isDark && !hasCustomBg ? { backgroundColor: '#0a0a0a' } : {})
                        }}
                      >
                        {displayValue}
                      </TableCell>
                    );
                  })}
                  <TableCell
                    className={
                      tabStyles.totalColumn +
                      ' ' + tabStyles.dataCellTypography +
                      (props.itemNum !== 0 && cell === 'date' ? ' ' + tabStyles.textDim : '')
                    }
                    align="right"
                    data-row={cell}
                    style={{
                      ...(isDark ? { backgroundColor: '#1A1A1A', color: '#E6E6E6' } : {})
                    }}
                  >
                    {getSumCellValue(cell, table[row])}
                  </TableCell>
                </TableRow>
              ))
            ))}
          </TableBody>
        </Table>
      </TableContainer>
  );
};

DataTableComponent.propTypes = {
  origin: PropTypes.string,
  table: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  itemNum: PropTypes.number
};

export default DataTableComponent;
